// Barra de abertos (modelo Zendesk) — faixa de abas logo abaixo do topo com
// tudo o que está em andamento: cards abertos, conversas (a atual e as com
// rascunho) e formulários pela metade. Clique leva de volta e reencontra tudo;
// × tira da barra (com rascunho, pergunta antes de descartar). Só aparece
// quando há algo aberto. Dados: lib/openTabs.ts.

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FilePlus2, LayoutGrid, MessageCircle, X } from 'lucide-react';
import { cn } from '../ui/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { confirm } from '../../hooks/useConfirm';
import { PERSISTED_EVENT } from '../../hooks/usePersistedState';
import { OPEN_TABS_EVENT } from '../../lib/conversationBridge';
import { onDraftChange } from '../tabs/atendimento/conversationMemory';
import { collectOpenTabs, type OpenTab, type OpenTabKind } from '../../lib/openTabs';

const MAX_VISIBLE = 6;

const KIND_ICON: Record<OpenTabKind, typeof MessageCircle> = {
  conversation: MessageCircle,
  card: LayoutGrid,
  form: FilePlus2,
};

export default function OpenTabsBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState<OpenTab[]>(() => collectOpenTabs());

  const refresh = useCallback(() => setTabs(collectOpenTabs()), []);
  useEffect(() => { refresh(); }, [location.pathname, location.search, refresh]);
  useEffect(() => {
    window.addEventListener(OPEN_TABS_EVENT, refresh);
    window.addEventListener(PERSISTED_EVENT, refresh);
    const offDraft = onDraftChange(refresh);
    return () => {
      window.removeEventListener(OPEN_TABS_EVENT, refresh);
      window.removeEventListener(PERSISTED_EVENT, refresh);
      offDraft();
    };
  }, [refresh]);

  if (tabs.length === 0) return null;

  const isActive = (t: OpenTab) => t.isActive(location.pathname);
  // A aba da tela atual sempre aparece, mesmo que tenha chegado por último.
  let visible = tabs.slice(0, MAX_VISIBLE);
  const active = tabs.find(isActive);
  if (active && !visible.includes(active)) visible = [...visible.slice(0, MAX_VISIBLE - 1), active];
  const overflow = tabs.filter((t) => !visible.includes(t));

  const closeTab = async (t: OpenTab) => {
    if (t.hasDraft) {
      const ok = await confirm({
        title: 'Descartar o que estava em andamento?',
        description: `"${t.label}" tem algo digitado que ainda não foi enviado. Fechar a aba descarta.`,
        confirmText: 'Descartar',
        variant: 'destructive',
      });
      if (!ok) return;
    }
    t.close();
    refresh();
  };

  const renderTab = (t: OpenTab) => {
    const Icon = KIND_ICON[t.kind];
    const on = isActive(t);
    return (
      <div
        key={t.id}
        data-testid="open-tab"
        data-active={on || undefined}
        className={cn(
          'group flex items-center gap-1.5 h-7 pl-2 pr-1 rounded-md border text-xs min-w-[96px] max-w-[220px] flex-shrink transition-colors',
          on
            ? 'border-indigo-200 bg-indigo-50 text-indigo-800'
            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
        )}
      >
        <button
          type="button"
          onClick={() => navigate(t.path)}
          className="flex items-center gap-1.5 min-w-0 flex-1 text-xs"
          title={[t.label, t.sub].filter(Boolean).join(' · ')}
        >
          <Icon className="size-3.5 flex-shrink-0 opacity-70" />
          {/* Título tem prioridade: o setor encolhe primeiro. */}
          <span className="truncate font-medium min-w-[4rem]">{t.label}</span>
          {t.sub && <span className="text-[11px] text-slate-400 min-w-0 max-w-[90px] truncate flex-shrink-[4]">{t.sub}</span>}
          {t.hasDraft && <span className="size-1.5 rounded-full bg-amber-500 flex-shrink-0" aria-label="Não enviado" />}
        </button>
        <button
          type="button"
          onClick={() => closeTab(t)}
          disabled={t.closeDisabled}
          aria-label={`Fechar ${t.label}`}
          title={t.closeDisabled ? 'Feche o card para tirar da barra' : 'Fechar'}
          className="size-5 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <X className="size-3" />
        </button>
      </div>
    );
  };

  return (
    <div
      data-testid="open-tabs-bar"
      data-open-tabs-bar
      className="flex items-center gap-1.5 px-4 lg:px-6 py-1.5 border-b border-slate-200 bg-slate-50/80 dark:bg-white/[0.02] overflow-hidden flex-shrink-0"
    >
      {visible.map(renderTab)}
      {overflow.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="open-tabs-more"
              className="h-7 px-2 rounded-md border border-slate-200 bg-white text-xs font-medium text-slate-600 hover:bg-slate-50 flex-shrink-0"
            >
              +{overflow.length}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {overflow.map((t) => {
              const Icon = KIND_ICON[t.kind];
              return (
                <DropdownMenuItem key={t.id} onSelect={() => navigate(t.path)} className="gap-2 text-xs">
                  <Icon className="size-3.5 opacity-70" />
                  <span className="truncate flex-1">{t.label}{t.sub ? ` · ${t.sub}` : ''}</span>
                  {t.hasDraft && <span className="size-1.5 rounded-full bg-amber-500" />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
